import apiClient from './client';
import type {
  ContentLanguageAvailability,
  CriteriaCatalogItem,
  GameMode,
  ImpactRule,
  Language,
  UiTranslationBundle,
  UiTranslationEntry,
  UpsertCriteriaTranslationRequest,
  UpsertImpactRulesRequest,
  UpsertUiTranslationRequest,
  UpsertVerbatimTranslationRequest,
  VerbatimRule,
} from '@business-game/shared';

export const contentApi = {
  async getPublicUiBundle(language: Language, gameMode?: GameMode): Promise<UiTranslationBundle> {
    const response = await apiClient.get('/content/ui-translations/public', {
      params: { language, gameMode },
    });
    return response.data.data;
  },

  async getLanguageAvailability(gameMode: GameMode): Promise<ContentLanguageAvailability[]> {
    const response = await apiClient.get(`/content/language-availability/${gameMode}`);
    return response.data.data;
  },

  async listCriteriaCatalog(gameMode?: GameMode): Promise<CriteriaCatalogItem[]> {
    const response = await apiClient.get('/content/criteria-catalog', {
      params: { gameMode },
    });
    return response.data.data;
  },

  async createCriteriaCatalog(data: { code: string; gameMode: GameMode; order?: number; isActive?: boolean }): Promise<CriteriaCatalogItem> {
    const response = await apiClient.post('/content/criteria-catalog', data);
    return response.data.data;
  },

  async updateCriteriaCatalog(id: number, data: { code?: string; gameMode?: GameMode; order?: number; isActive?: boolean }): Promise<CriteriaCatalogItem> {
    const response = await apiClient.put(`/content/criteria-catalog/${id}`, data);
    return response.data.data;
  },

  async upsertCriteriaTranslation(data: UpsertCriteriaTranslationRequest) {
    const response = await apiClient.put('/content/criteria-translations', data);
    return response.data.data;
  },

  async getImpactRules(criteriaId: number): Promise<ImpactRule[]> {
    const response = await apiClient.get(`/content/impacts/${criteriaId}`);
    return response.data.data;
  },

  async replaceImpactRules(criteriaId: number, data: UpsertImpactRulesRequest): Promise<ImpactRule[]> {
    const response = await apiClient.put(`/content/impacts/${criteriaId}`, data);
    return response.data.data;
  },

  async listVerbatimRules(gameMode?: GameMode, typeId?: number): Promise<VerbatimRule[]> {
    const response = await apiClient.get('/content/verbatim-rules', {
      params: { gameMode, typeId },
    });
    return response.data.data;
  },

  async createVerbatimRule(data: { gameMode: GameMode; typeId: number; bound: number; isActive?: boolean }): Promise<VerbatimRule> {
    const response = await apiClient.post('/content/verbatim-rules', data);
    return response.data.data;
  },

  async updateVerbatimRule(id: number, data: { gameMode?: GameMode; typeId?: number; bound?: number; isActive?: boolean }): Promise<VerbatimRule> {
    const response = await apiClient.put(`/content/verbatim-rules/${id}`, data);
    return response.data.data;
  },

  async upsertVerbatimTranslation(data: UpsertVerbatimTranslationRequest) {
    const response = await apiClient.put('/content/verbatim-translations', data);
    return response.data.data;
  },

  async listUiTranslations(language: Language, namespace?: string, gameMode?: GameMode): Promise<UiTranslationEntry[]> {
    const response = await apiClient.get('/content/ui-translations', {
      params: { language, namespace, gameMode },
    });
    return response.data.data;
  },

  async upsertUiTranslation(data: UpsertUiTranslationRequest): Promise<UiTranslationEntry> {
    const response = await apiClient.put('/content/ui-translations', data);
    return response.data.data;
  },
};
